import Loading from "common/Loading";
import Legend from "common/graphs/Legend";
import Chart from "./Chart";
import { getInRangeItems, parseStatsCpuData } from "./api";
import { useContext, useEffect, useState } from "react";
import { useScreenSize } from "common/hooks";
import useDebouncer from "common/hooks/debouncer";
import ActionsContext from "common/ActionsContext";
import styled from "styled-components";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import { PERIOD_TO_LINES } from "common/constants";
import { getBqnIdCmdPart, getSampleSize } from "common/api";

const Empty = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const ChartContainer = styled.div`
  flex: 100% 1 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

const LegendContainer = styled.div`
  width: 100%;
  padding: 0px 55px 0px 55px;
`;

const ChartAndLegendContainer = styled("div")(({ big }) => ({
  display: "flex",
  flexDirection: 'column',
  whiteSpace: 'nowrap',
  height: big ? 500 : 300,
}));

export const CpuChart = ({
  bqnId,
  period,
  setCpuChoices,
  range,
  initialRange,
  chart,
  cpu,
  mode,
  scale,
  big,
}) => {
  const [data, setData] = useState({ fields: [], items: [] });
  const [filteredItems, setFilteredItems] = useState([]);
  const [newFields, setNewFields] = useState([]);
  const [legendFieldsSet, setLegendFieldsSet] = useState(new Set());
  const colorsSet = login.isTheme("light") ? colors : colorsDark;
  const [loading, setLoading] = useState(false);
  const { width: windowWidth } = useScreenSize();
  const debounce = useDebouncer(500 /*ms*/);
  const actions = useContext(ActionsContext);

  const doLoad = () => {
    setLoading(true);
    setData({ fields: [], items: [] });
    const { from, to } = initialRange;
    const chartN = chart === "busy" ? "idle" : chart;
    ifCl
      .run(
        `show statistics cpu ${chartN} lines ${
          PERIOD_TO_LINES[period]
        }${getBqnIdCmdPart(mode, bqnId)}`,
      )
      .then((response) => {
        const { items, fields } = parseStatsCpuData(response, from, to);
        const [, ...cpuNames] = fields;
        if (cpuNames?.length > 0) {
          /** Nodes can have different CPU counts, so add its own CPU options */
          setCpuChoices((current) => {
            const currentCpus = current.map(({ value }) => value);
            const cpuChoicesUpdate = Array.from(
              new Set([...currentCpus, ...cpuNames]),
            );

            if (currentCpus.join() === cpuChoicesUpdate.join()) {
              return current;
            } else {
              return cpuChoicesUpdate.map((name) => ({
                label: name,
                value: name,
              }));
            }
          });
        }
        setData({ items, fields });
      })
      .catch((error) => {
        console.warn(
          `Cpu statistics lines retrieval failed: ${
            !error.message ? "unknown reason" : error.message
          }`,
        );
        setLoading(false);
      });
  };

  const { fields, items } = data;
  const newFieldsValues = fields.map((field, index) => {
    const colorIndex = index % colorsSet.length;
    return {
      name: field,
      label: field,
      color: colorsSet[colorIndex],
    };
  });

  const loadStats = () => {
    if (fields.length > 0) {
      const selectedRange = range || initialRange;
      const POINTS_PER_TILE_CHART =
        windowWidth / (1 + Math.ceil(fields.length / 25));
      const sampleSize = getSampleSize(
        selectedRange,
        POINTS_PER_TILE_CHART,
        period,
      );
      const selectedFields =
        cpu === "All" ? fields : fields.filter((item) => item === cpu);

      const fieldsSet = new Set(selectedFields);
      setLegendFieldsSet(fieldsSet);
      const newFields = newFieldsValues.filter(({ name }) =>
        fieldsSet.has(name),
      );
      setNewFields(newFields);
      const newItems = getInRangeItems({
        items,
        range: selectedRange,
        fields: newFields,
        sampleSize,
        cpu,
        chart,
      });
      setFilteredItems(newItems);
    }
    setLoading(false);
  };

  useEffect(() => {
    debounce(() => loadStats());
  }, [range, cpu, fields, items, windowWidth, initialRange, period, chart]);

  useEffect(() => {
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [bqnId, chart, initialRange]);

  return (
    <ChartAndLegendContainer big={big}>
      {!loading ? (
        <LegendContainer>
          <Legend
            fields={newFields}
            value={legendFieldsSet}
            onChange={(filterSet) => {
              setLegendFieldsSet(filterSet);
            }}
          />
        </LegendContainer>
      ) : null}
      <ChartContainer>
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>{"No Data Available"}</Empty>
        ) : (
          <Chart
            fixedYRange={scale === "Full" ? [0, 100] : [0, null]}
            items={filteredItems}
            fields={newFields.filter(({ name }) => legendFieldsSet.has(name))}
          />
        )}
      </ChartContainer>
    </ChartAndLegendContainer>
  );
};
